import { forwardRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Calendar } from "./Calendar";
import {
  formatDateTyping,
  formatIsoDateForDisplay,
  normalizeDateForPayload,
} from "@/lib/format";
import { cn } from "@/lib/utils";

export interface DateFieldProps {
  /** MM/DD/YYYY or empty. */
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  invalid?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Restrict picker to today+. */
  futureOnly?: boolean;
  /** Restrict picker to today-. */
  pastOnly?: boolean;
  id?: string;
  ariaInvalid?: boolean;
  /** Show an X next to the calendar icon when there's a value, so staff
   *  can wipe the date without triple-clicking. Defaults to true. */
  clearable?: boolean;
}

export const DateField = forwardRef<HTMLInputElement, DateFieldProps>(
  (
    {
      value,
      onChange,
      onBlur,
      invalid,
      disabled,
      placeholder = "MM/DD/YYYY",
      futureOnly,
      pastOnly,
      id,
      clearable = true,
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);

    const iso = normalizeDateForPayload(value);
    const showClear = clearable && Boolean(value) && !disabled;

    return (
      <div
        className={cn(
          "flex h-10 items-stretch rounded-xl border border-border bg-foreground/[0.03] focus-within:ring-2 focus-within:ring-ring",
          invalid && "border-destructive focus-within:ring-destructive",
          disabled && "opacity-60",
        )}
      >
        <Input
          ref={ref}
          id={id}
          type="text"
          inputMode="numeric"
          placeholder={placeholder}
          maxLength={10}
          autoComplete="off"
          disabled={disabled}
          value={value}
          onChange={(e) => onChange(formatDateTyping(e.target.value))}
          onBlur={() => {
            const norm = normalizeDateForPayload(value);
            if (norm) onChange(formatIsoDateForDisplay(norm));
            onBlur?.();
          }}
          aria-invalid={invalid || undefined}
          className="h-9 flex-1 rounded-none border-0 bg-transparent focus:ring-0"
        />
        {showClear ? (
          <button
            type="button"
            aria-label="Clear date"
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onChange("")}
            className="flex w-9 items-center justify-center border-l border-border text-muted-foreground transition hover:bg-foreground/[0.06] hover:text-destructive"
          >
            <X className="h-3.5 w-3.5" strokeWidth={2.4} />
          </button>
        ) : null}
        <Popover.Root open={open} onOpenChange={setOpen}>
          <Popover.Trigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label="Open calendar"
              className="flex w-10 items-center justify-center border-l border-border text-muted-foreground transition hover:bg-foreground/[0.06] hover:text-primary disabled:cursor-not-allowed"
            >
              <CalendarIcon className="h-4 w-4" />
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              align="end"
              sideOffset={6}
              className="z-50 animate-in fade-in"
            >
              <Calendar
                value={iso}
                blockPast={futureOnly}
                blockFuture={pastOnly}
                onSelect={(picked) => {
                  onChange(formatIsoDateForDisplay(picked));
                  setOpen(false);
                }}
              />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </div>
    );
  },
);
DateField.displayName = "DateField";
